import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { usePostHog } from "@/analytics/usePostHog";
import { TriangleAlert } from "lucide-react";
import { SEOHead } from "@/components/SEOHead";
import { trackMetaViewContent } from "@/analytics/metaEvents";
import { DoctorVisitsHero } from "@/components/doctor-visits/DoctorVisitsHero";
import { VisitLocations } from "@/components/doctor-visits/VisitLocations";
import { VisitJourney } from "@/components/doctor-visits/VisitJourney";
import { DoctorVisitsValue } from "@/components/doctor-visits/DoctorVisitsValue";
import {
  DoctorVisitsFaq,
  type DoctorVisitsFaqItem,
} from "@/components/doctor-visits/DoctorVisitsFaq";
import { DoctorVisitsFinalCta } from "@/components/doctor-visits/DoctorVisitsFinalCta";
import { DoctorVisitLocationGuides } from "@/components/doctor-visits/DoctorVisitLocationGuides";
import { localizePath } from "@/lib/localeRoutes";
import { buildSeoUrls } from "@/lib/seo";
import {
  breadcrumbSchema,
  faqSchema,
} from "@/lib/structuredData";
import { buildDoctorVisitsServiceSchema } from "@/lib/doctorVisitsSeo";

const DoctorVisits = () => {
  const { t, i18n } = useTranslation("doctorVisits");
  const { pathname } = useLocation();
  const posthog = usePostHog();
  const locale = i18n.language;
  const faqs = t("faq.items", {
    returnObjects: true,
  }) as DoctorVisitsFaqItem[];
  const { canonicalUrl } = buildSeoUrls(pathname);

  useEffect(() => {
    posthog?.capture("service_page_viewed", {
      service: "doctor_visits",
      locale,
    });

    // Mirror to Meta as ViewContent. Page-level identifier only — no health data.
    // No-ops until the visitor has consented (see analytics/metaEvents.ts).
    trackMetaViewContent({ contentName: "doctor-visits", category: "service" });
  }, [locale, posthog]);

  const serviceSchema = buildDoctorVisitsServiceSchema({
    canonicalUrl,
    name: t("seo.title"),
    description: t("seo.description"),
    serviceType: t("seo.serviceType"),
  });
  const crumbs = breadcrumbSchema(
    [
      {
        name: t("breadcrumb"),
        path: localizePath("/doctor-visits", locale),
      },
    ],
    localizePath("/", locale),
  );

  return (
    <>
      <SEOHead
        title={t("seo.title")}
        description={t("seo.description")}
        jsonLd={[serviceSchema, faqSchema(faqs), crumbs]}
      />
      <DoctorVisitsHero />
      <VisitLocations />
      <DoctorVisitLocationGuides />
      <VisitJourney />
      <DoctorVisitsValue />

      <section className="px-4 py-10 sm:px-6 sm:py-12 lg:px-8">
        <aside className="mx-auto flex max-w-7xl gap-4 border-y border-destructive/25 py-7 sm:items-start sm:gap-5">
          <span className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-destructive/10 text-destructive">
            <TriangleAlert className="h-5 w-5" aria-hidden="true" />
          </span>
          <div>
            <h2 className="text-lg font-semibold text-foreground">
              {t("emergency.title")}
            </h2>
            <p className="mt-2 max-w-4xl leading-7 text-muted-foreground">
              {t("emergency.description")}{" "}
              <span className="font-medium text-foreground">
                {t("emergency.action")}
              </span>
            </p>
          </div>
        </aside>
      </section>

      <DoctorVisitsFaq items={faqs} />
      <DoctorVisitsFinalCta />
    </>
  );
};

export default DoctorVisits;
